<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { LiveRoom } from "@api-gen/live_pb";
import { MatchStatus, type Match } from "@api-gen/sports_pb";
import { api, formatCount } from "@/api";
import { officialLiveRooms } from "@/liveRooms";
import {
  mergeHomepageOngoingMatches,
  responseNowMillis,
} from "@/matchDisplay";

type Recommendation = "热门" | "足球" | "篮球";
type LiveCard = {
  id: string;
  roomId: string;
  poster: string;
  title: string;
  presenter: string;
  viewers: number;
  sport: "足球" | "篮球" | "综合";
};

const matches = ref<Match[]>([]);
const rooms = ref<LiveRoom[]>([]);
const currentHero = ref(0);
const activeRecommendation = ref<Recommendation>("热门");
let heroTimer: ReturnType<typeof setInterval> | undefined;

const posterFiles = Array.from(
  { length: 27 },
  (_, index) => `/demo-assets/poster-${String(index + 1).padStart(2, "0")}.jpg`,
);
const fallbackPoster = "/demo-assets/poster-01.jpg";

const officialRooms = computed(() => {
  const ongoingMatchIds = new Set(matches.value.map((match) => match.matchId));
  return officialLiveRooms(
    rooms.value.filter(
      (room) => room.matchId && ongoingMatchIds.has(room.matchId),
    ),
    matches.value.map((match) => match.liveRoomId).filter(Boolean),
  );
});
const desktopMatches = computed(() => matches.value.slice(0, 4));
const mobileMatches = computed(() => matches.value.slice(0, 2));

const cards = computed<LiveCard[]>(() => {
  return officialRooms.value.map((room, index) => ({
    id: room.roomId,
    roomId: room.roomId,
    poster:
      room.coverUrl ||
      posterFiles[index % posterFiles.length] ||
      fallbackPoster,
    title: room.title || "官方直播",
    presenter: "官方直播",
    viewers: Math.max(0, Number(room.viewerCount)),
    sport: /篮球|NBA|CBA|WNBA/i.test(`${room.title} ${room.tags.join(" ")}`)
      ? "篮球"
      : /足球|英超|西甲|意甲|德甲|法甲|欧冠/i.test(
            `${room.title} ${room.tags.join(" ")}`,
          )
        ? "足球"
        : "综合",
  }));
});

const featuredCards = computed(() => cards.value.slice(0, 5));
const activeFeature = computed(() => featuredCards.value[currentHero.value]);
const mobileSlides = computed(() => {
  const list = featuredCards.value;
  if (list.length <= 3) return list;
  const previous = (currentHero.value - 1 + list.length) % list.length;
  const next = (currentHero.value + 1) % list.length;
  return [list[previous], list[currentHero.value], list[next]].filter(
    (card): card is LiveCard => Boolean(card),
  );
});
const recommendationCards = computed(() => {
  if (activeRecommendation.value === "热门") return cards.value;
  return cards.value.filter(
    (card) => card.sport === activeRecommendation.value,
  );
});
const desktopGroups = computed(() => [
  {
    title: "足球直播",
    icon: "football",
    cards: cards.value.filter((card) => card.sport === "足球"),
  },
  {
    title: "篮球直播",
    icon: "basketball",
    cards: cards.value.filter((card) => card.sport === "篮球"),
  },
  {
    title: "综合直播",
    icon: "general",
    cards: cards.value.filter((card) => card.sport === "综合"),
  },
]);

function matchAt(index: number) {
  return matches.value[index];
}

function competitionName(index: number) {
  return matchAt(index)?.competition?.name || "赛事";
}

function teamName(index: number, side: "home" | "away") {
  const match = matchAt(index);
  const team = side === "home" ? match?.homeTeam : match?.awayTeam;
  return team?.name || "队伍待定";
}

function teamScore(index: number, side: "home" | "away") {
  const match = matchAt(index);
  const value = side === "home" ? match?.score?.home : match?.score?.away;
  return value ?? "—";
}

function matchTime(index: number) {
  const scheduled = matchAt(index)?.scheduledAt;
  if (!scheduled) return "时间待定";
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(
    new Date(Number(scheduled.seconds) * 1000 + scheduled.nanos / 1_000_000),
  );
}

function liveTarget(roomId?: string) {
  return roomId ? `/live/${roomId}` : "/live";
}

function matchTarget(match?: Match) {
  if (match?.liveRoomId) return `/live/${match.liveRoomId}`;
  return match?.matchId ? `/match/${match.matchId}` : "/matches";
}

function advanceHero() {
  const length = featuredCards.value.length;
  currentHero.value = length > 1 ? (currentHero.value + 1) % length : 0;
}

async function fetchLiveMatches() {
  const loaded: Match[] = [];
  let pageToken = "";
  do {
    const response = await api.sports.listMatches({
      status: MatchStatus.LIVE,
      page: { pageSize: 100, pageToken },
    });
    loaded.push(...response.matches);
    pageToken = response.page?.nextPageToken ?? "";
  } while (pageToken);
  return loaded;
}

async function loadHome() {
  try {
    const [home, live, liveMatches] = await Promise.all([
      api.content.getHome({ sport: 0 }),
      api.live.listLiveRooms({}),
      fetchLiveMatches(),
    ]);
    matches.value = mergeHomepageOngoingMatches(
      home.featuredMatches,
      liveMatches,
      responseNowMillis(home.metadata),
    );
    rooms.value =
      home.featuredLiveRooms.length > 0 ? home.featuredLiveRooms : live.rooms;
  } catch {
    // The local visual demo remains usable while the API is restarting.
  }
}

onMounted(() => {
  void loadHome();
  heroTimer = setInterval(advanceHero, 4500);
});

onBeforeUnmount(() => {
  if (heroTimer) clearInterval(heroTimer);
});
</script>

<template>
  <section class="reference-home">
    <div class="pc-home">
      <section class="pc-showcase" aria-label="热门直播推荐">
        <div class="showcase-inner">
          <RouterLink
            v-if="activeFeature"
            class="showcase-main"
            :to="liveTarget(activeFeature.roomId)"
          >
            <img :src="activeFeature.poster" :alt="activeFeature.title" />
            <span>进入直播间</span>
          </RouterLink>
          <div v-else class="showcase-main pc-home-empty">
            <strong>当前暂无官方直播</strong>
          </div>
          <div class="showcase-thumbs">
            <button
              v-for="(card, index) in featuredCards"
              :key="card.id"
              :class="{ active: currentHero === index }"
              type="button"
              @click="currentHero = index"
            >
              <img :src="card.poster" :alt="card.title" />
              <small>{{ card.title }}</small>
            </button>
          </div>
        </div>
      </section>

      <section class="pc-content">
        <div class="hot-matches-title" aria-label="热门赛事">
          <span></span>
        </div>
        <div class="pc-match-grid">
          <RouterLink
            v-for="(match, index) in desktopMatches"
            :key="match.matchId"
            class="pc-match-card"
            :to="matchTarget(match)"
          >
            <header>
              <b>◉</b>
              <span>{{ competitionName(index) }}</span>
              <time>{{ matchTime(index) }}</time>
            </header>
            <div>
              <p>
                <i :class="`team-dot dot-${index + 1}`"></i>
                <span>{{ teamName(index, "home") }}</span>
                <strong>{{ teamScore(index, "home") }}</strong>
              </p>
              <p>
                <i :class="`team-dot dot-${index + 2}`"></i>
                <span>{{ teamName(index, "away") }}</span>
                <strong>{{ teamScore(index, "away") }}</strong>
              </p>
            </div>
          </RouterLink>
          <p v-if="!desktopMatches.length" class="pc-match-empty">
            暂无进行中的比赛
          </p>
        </div>

        <section class="pc-live-section">
          <header class="pc-section-title live-title">
            <h2><span class="section-symbol camera"></span>正在热播</h2>
            <i></i>
            <RouterLink to="/live">查看更多 &gt;</RouterLink>
          </header>
          <div class="pc-live-grid">
            <RouterLink
              v-for="card in cards"
              :key="card.id"
              class="pc-live-card"
              :to="liveTarget(card.roomId)"
            >
              <img :src="card.poster" :alt="card.title" loading="lazy" />
              <h3>{{ card.title }}</h3>
              <footer>
                <span class="mini-avatar">{{
                  card.presenter.slice(0, 1)
                }}</span>
                <span>{{ card.presenter }}</span>
                <b>🔥 {{ formatCount(card.viewers) }}</b>
              </footer>
            </RouterLink>
            <p v-if="!cards.length" class="pc-match-empty">当前暂无官方直播</p>
          </div>
        </section>

        <section
          v-for="group in desktopGroups"
          v-show="group.cards.length"
          :key="group.title"
          class="pc-live-section grouped-section"
        >
          <header class="pc-section-title">
            <h2>
              <span :class="['section-symbol', group.icon]"></span
              >{{ group.title }}
            </h2>
            <i></i>
            <RouterLink to="/live">查看更多 &gt;</RouterLink>
          </header>
          <div class="pc-live-grid">
            <RouterLink
              v-for="card in group.cards"
              :key="`${group.title}-${card.id}`"
              class="pc-live-card"
              :to="liveTarget(card.roomId)"
            >
              <img :src="card.poster" :alt="card.title" loading="lazy" />
              <h3>{{ card.title }}</h3>
              <footer>
                <span class="mini-avatar">{{
                  card.presenter.slice(0, 1)
                }}</span>
                <span>{{ card.presenter }}</span>
                <b>🔥 {{ formatCount(card.viewers) }}</b>
              </footer>
            </RouterLink>
          </div>
        </section>
      </section>

      <footer class="pc-site-footer">
        <p>
          特别声明：178直播仅展示平台自有或已获得明确授权的赛事直播内容，不接入来源不明或未经授权的播放地址。
        </p>
        <p>
          请各位网友遵守当地国家法律法规，请勿用于非法用途，所有内容仅供本地功能测试。
        </p>
        <p>Copyright © 2026 178 All Rights Reserved · 隐私协议 · 应用权限</p>
      </footer>
    </div>

    <div class="mobile-home">
      <section class="mobile-hero">
        <div
          v-if="mobileSlides.length"
          :class="['mobile-hero-track', { compact: mobileSlides.length <= 2 }]"
        >
          <RouterLink
            v-for="(card, index) in mobileSlides"
            :key="`${card.id}-${index}`"
            :class="['mobile-hero-slide', { current: index === 1 }]"
            :to="liveTarget(card.roomId)"
          >
            <img :src="card.poster" :alt="card.title" />
            <span class="mobile-play"></span>
            <small>{{ card.title }}</small>
            <i v-if="index === 1">
              <b></b><b></b><b class="active"></b><b></b>
            </i>
          </RouterLink>
        </div>
        <div v-else class="mobile-hero-empty">当前暂无官方直播</div>
      </section>

      <section class="mobile-hot">
        <header class="mobile-row-title">
          <h2><span>♨</span>热门比赛</h2>
          <div>
            <button type="button">《</button><button type="button">》</button>
          </div>
        </header>
        <div class="mobile-match-grid">
          <RouterLink
            v-for="(match, index) in mobileMatches"
            :key="match.matchId"
            class="mobile-match-card"
            :to="matchTarget(match)"
          >
            <header>
              <span>{{ competitionName(index) }}</span>
              <time>{{ matchTime(index) }}</time>
            </header>
            <p>
              <i :class="`mobile-flag flag-${index + 1}`"></i>
              <span>{{ teamName(index, "home") }}</span>
              <strong>{{ teamScore(index, "home") }}</strong>
            </p>
            <p>
              <i :class="`mobile-flag flag-${index + 3}`"></i>
              <span>{{ teamName(index, "away") }}</span>
              <strong>{{ teamScore(index, "away") }}</strong>
            </p>
          </RouterLink>
          <p v-if="!mobileMatches.length" class="mobile-match-empty">
            暂无进行中的比赛
          </p>
        </div>
      </section>

      <section class="mobile-recommendations">
        <header class="mobile-row-title recommendations-title">
          <h2><span>▣</span>今日推荐</h2>
          <div class="mobile-filters">
            <button
              v-for="filter in ['热门', '足球', '篮球'] as Recommendation[]"
              :key="filter"
              :class="{ active: activeRecommendation === filter }"
              type="button"
              @click="activeRecommendation = filter"
            >
              <i>{{
                filter === "热门" ? "♨" : filter === "足球" ? "⚽" : "◉"
              }}</i>
              {{ filter }}
            </button>
          </div>
        </header>
        <div class="mobile-live-grid">
          <RouterLink
            v-for="card in recommendationCards"
            :key="`mobile-${card.id}`"
            class="mobile-live-card"
            :to="liveTarget(card.roomId)"
          >
            <div class="mobile-card-cover">
              <img :src="card.poster" :alt="card.title" loading="lazy" />
              <footer>
                <span>{{ card.presenter }}</span>
                <b>🔥 {{ formatCount(card.viewers) }}</b>
              </footer>
            </div>
            <p><i></i>{{ card.title }}</p>
          </RouterLink>
          <p v-if="!recommendationCards.length" class="mobile-match-empty">
            当前暂无直播推荐
          </p>
        </div>
      </section>
    </div>
  </section>
</template>
